import { Component } from 'react'
import styled from 'styled-components'
import { parseISO, format } from 'date-fns'

import DateRangeCalendar from 'components/form/DateRangeCalendar'
import InvisibleButton from 'components/display/InvisibleButton'
import AvailableWeekends from '../common/AvailableWeekends'

import FilterButton from './FilterButton'

const Wrapper = styled.div`
  /* Breathe the calendar grid away from the prev/next nav + month
     caption row above it. Matches the host-availability surface. */
  .rdp-month_grid {
    margin-top: 10px;
  }
`

const ViewTabs = styled.div`
  margin: 0 -16px;
  padding: 0 16px;

  border-bottom: 1px solid ${({ theme }) => theme?.['-ds-neutral-300']?.value};

  margin-bottom: 24px;

  & > *:not(:last-child) {
    margin-right: 24px;
  }
`

const Tab = styled(InvisibleButton)`
  position: relative;
  padding: 12px 0;
  font-weight: 400;
  color: ${({ theme, active }) =>
    active
      ? theme?.['-ds-core-400']?.value
      : theme?.['-ds-neutral-700']?.value};

  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xs']?.['font-size']?.value};

  ${({ active, theme }) =>
    active &&
    `
    border-bottom: 2px solid ${theme?.['-ds-core-400']?.value};
  `}
`

const SEARCH_TYPE_SPECIFIC_DATES = 'specific-dates'
const SEARCH_TYPE_AVAILABLE_WEEKENDS = 'available-weekends'

// Filter dates arrive as ISO strings (deserialized from the URL) or as
// Dates (a calendar selection still in flight); the calendar wants Dates.
const toDate = (value) => {
  if (!value) return null
  return typeof value === 'string' ? parseISO(value) : value
}

const getInitialDateSearchType = (props) => {
  if (props.weekendMonths?.length > 0) {
    return SEARCH_TYPE_AVAILABLE_WEEKENDS
  }

  return SEARCH_TYPE_SPECIFIC_DATES
}

class DatesFilter extends Component {
  constructor(props) {
    super(props)
    this.state = {
      dateSearchType: getInitialDateSearchType(props),
      startDate: toDate(props.startDate),
      endDate: toDate(props.endDate),
      weekendMonths: props?.weekendMonths || null,
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({
      startDate: toDate(nextProps.startDate),
      endDate: toDate(nextProps.endDate),
      weekendMonths: nextProps?.weekendMonths || null,
    })
  }

  onApply = () => {
    const { dateSearchType, weekendMonths } = this.state
    const { onChange } = this.props

    if (dateSearchType === SEARCH_TYPE_AVAILABLE_WEEKENDS) {
      onChange({ weekendMonths })
      return
    }

    const { startDate, endDate } = this.state
    onChange({ startDate, endDate })
  }

  onReset = () => {
    const { startDate, endDate } = this.props
    this.setState({
      startDate: toDate(startDate),
      endDate: toDate(endDate),
    })
  }

  onDatesChange = (startDate, endDate) => {
    this.setState({ startDate, endDate })
  }

  onDatesClear = () => {
    const { onChange } = this.props
    this.setState({
      startDate: null,
      endDate: null,
      weekendMonths: null,
    })
    onChange({ startDate: null, endDate: null, weekendMonths: null })
  }

  getLabel = () => {
    const { startDate, endDate, dateSearchType, weekendMonths } = this.state
    if (
      dateSearchType === SEARCH_TYPE_AVAILABLE_WEEKENDS &&
      weekendMonths?.length > 0
    ) {
      return 'Weekends'
    }
    const formatDate = (d) => format(d, 'dd MMM')
    const fomattedStart = startDate ? formatDate(startDate) : 'Start'
    const fomattedEnd = endDate ? formatDate(endDate) : 'End'
    return startDate || endDate ? `${fomattedStart} - ${fomattedEnd}` : 'Dates'
  }

  render() {
    const { isActive, ...props } = this.props
    const { dateSearchType, weekendMonths, startDate, endDate } = this.state

    return (
      <FilterButton
        label={this.getLabel()}
        onApply={this.onApply}
        onClear={this.onDatesClear}
        onReset={this.onReset}
        isActive={props.startDate || props.endDate || props.weekendMonths}
      >
        <Wrapper>
          <ViewTabs>
            <Tab
              active={dateSearchType === 'specific-dates'}
              onClick={() =>
                this.setState({ dateSearchType: SEARCH_TYPE_SPECIFIC_DATES })
              }
            >
              Specific dates
            </Tab>
            <Tab
              active={dateSearchType === 'available-weekends'}
              onClick={() =>
                this.setState({
                  dateSearchType: SEARCH_TYPE_AVAILABLE_WEEKENDS,
                })
              }
            >
              Available weekends
            </Tab>
          </ViewTabs>

          {dateSearchType === SEARCH_TYPE_SPECIFIC_DATES && (
            <DateRangeCalendar
              startDate={startDate}
              endDate={endDate}
              onChange={this.onDatesChange}
              numberOfMonths={2}
              minDate={new Date()}
              daySize={40}
            />
          )}

          {dateSearchType === SEARCH_TYPE_AVAILABLE_WEEKENDS && (
            <AvailableWeekends
              selectedValues={weekendMonths || []}
              onChange={({ value, checked }) => {
                if (checked) {
                  this.setState({
                    weekendMonths: [...(weekendMonths || []), value],
                  })
                } else {
                  this.setState({
                    weekendMonths: (weekendMonths || []).filter(
                      (v) => v !== value
                    ),
                  })
                }
              }}
            />
          )}
        </Wrapper>
      </FilterButton>
    )
  }
}

export default DatesFilter
